import type { TechnicalLogEntryDto } from './technical-log-entry.dto';
import type { TechnicalLogDetail, TechnicalLogEntry } from './technical-log-entry.model';

/** Come si rilegge un dettaglio scritto a stringa dalla versione precedente: un campo solo, quello. */
const LEGACY_DETAIL_FIELD = 'raw';

/**
 * Fra la riga di dominio, il record dell'archivio e il JSON che finisce nella segnalazione. E' una
 * classe statica pura: nessuna DI, nessuno stato.
 */
export class TechnicalLogMapper {
  static toDto(entry: TechnicalLogEntry): TechnicalLogEntryDto {
    return {
      at: entry.at,
      kind: entry.kind,
      message: entry.message,
      ...(entry.detail ? { detail: entry.detail } : {}),
    };
  }

  static toDomain(dto: TechnicalLogEntryDto): TechnicalLogEntry {
    return {
      at: dto.at,
      kind: dto.kind,
      message: dto.message,
      ...(dto.detail ? { detail: TechnicalLogMapper.toDetail(dto.detail) } : {}),
    };
  }

  /**
   * Il diario come lo legge chi riceve la segnalazione: un array JSON, una riga per fatto. E' JSON e non
   * testo incolonnato perche' a valle ci lavora una dashboard — filtrare per tipo, contare gli errori,
   * mettere in ordine i tempi sono conti sui campi, e da un testo si farebbero solo ri-analizzandolo.
   *
   * Il momento viaggia in ISO: una segnalazione si incrocia con i log del server, e il fuso del browser
   * non e' il suo.
   */
  static toJson(entries: readonly TechnicalLogEntryDto[]): string {
    if (entries.length === 0) return '';

    return `[${entries.map((entry) => TechnicalLogMapper.toJsonEntry(entry)).join(',')}]`;
  }

  /**
   * Le ultime righe che stanno nel budget, in ordine di tempo. Si taglia dal principio e non dalla fine
   * perche' di una finestra che precede un guasto la parte che lo spiega e' quella vicina al guasto.
   *
   * Cio' che esce e' JSON valido o niente: si tolgono righe intere, mai mezza riga — un array aperto e
   * non chiuso sarebbe illeggibile tanto quanto non mandarlo.
   */
  static toJsonWithin(entries: readonly TechnicalLogEntryDto[], budget: number): string {
    const kept: string[] = [];
    // Le due parentesi dell'array: ci sono anche quando dentro resta una riga sola.
    let used = 2;

    for (let index = entries.length - 1; index >= 0; index -= 1) {
      const entry = TechnicalLogMapper.toJsonEntry(entries[index]);
      // La virgola che unira' questa riga alla successiva.
      const cost = entry.length + (kept.length === 0 ? 0 : 1);

      if (used + cost > budget) break;

      kept.push(entry);
      used += cost;
    }

    if (kept.length === 0) return '';

    return `[${kept.reverse().join(',')}]`;
  }

  private static toJsonEntry(entry: TechnicalLogEntryDto): string {
    return JSON.stringify({
      at: new Date(entry.at).toISOString(),
      kind: entry.kind,
      message: entry.message,
      ...(entry.detail ? { detail: TechnicalLogMapper.toDetail(entry.detail) } : {}),
    });
  }

  /**
   * Il dettaglio nella forma a campi. Un record scritto dalla versione precedente lo porta gia'
   * appiattito a stringa: si avvolge invece di provare a indovinarne i campi — chi legge trova un oggetto
   * in ogni riga, e la riga vecchia dichiara di esserlo.
   */
  private static toDetail(detail: TechnicalLogDetail | string): TechnicalLogDetail {
    return typeof detail === 'string' ? { [LEGACY_DETAIL_FIELD]: detail } : detail;
  }
}
